import { isServer } from "solid-js/web";

import { useGlobalCacheCookie } from "@/hooks/useGlobalCacheCookies.ts";
import { useUrlStorage } from "@/hooks/useUrlStorage.ts";

import { APP_ID, LocalStorageKey, SERVER_URL } from "../constants/constants.ts";
import { getCurrentRequestLangKey, getLangKey } from "../i18n/index.ts";
import { deviceInfo } from "../stores/deviceStore.ts";
import { userStore } from "../stores/userStore.ts";
import { CheckHelper } from "./checkHelper.ts";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "./localStorage.ts";

/**
 * 提供API请求相关的工具函数和配置
 *
 * @version 1.0.0
 * @file 请求库工具函数
 * @author jaxson
 * @date 2025-03-21
 * @lastModified 2025-03-21
 */
export const appID = encodeAppid(Number(APP_ID));
const checkHelper = new CheckHelper();

export function getBaseHttpUrl() {
  // 如果是客户端环境，返回空字符串，让 getHallHttpUrl 函数添加完整的URL前缀
  if (!isServer) {
    return "";
  }

  const host = SERVER_URL ? `${SERVER_URL}` : "";
  return host;
}

// export function getInstanceConfig(id: string) {
//   return {
//     id,
//     timeout: 3000,
//     baseURL: getBaseHttpUrl(),
//     requestAdapter: axiosRequestAdapter(),
//     beforeRequest: beforeRequest,
//     responded: afterRequest(),
//     // cacheFor: {
//     //   // 统一设置POST的缓存模式
//     //   POST: {
//     //     mode: "restore",
//     //     expire: 60 * 10 * 1000,
//     //     tag: __APP_VERSION__,
//     //   },
//     //   // 统一设置HEAD请求的缓存模式
//     //   HEAD: 60 * 10 * 1000,
//     // },
//   };
// }

// function beforeRequest(methods: Method) {
//   const [servername, func] = methods.url.split("/");
//   methods.baseURL = `${methods.baseURL}${hallUrlSuffix(servername, func, methods.config.params)}`;
//   methods.config.data = {
//     _check: checkHelper.getNormalChecker(),
//     _param: Object.assign(
//       {
//         // host: location?.host ?? "",
//         noToken: methods.meta?.noToken ?? "", // 在meta中传入
//       },
//       methods.data ? removeObjEmpty(methods.data as object) : {},
//     ),
//   };
//   methods.config.params = {};
//   methods.url = "";
//   if (methods.config.shwoloading && typeof window !== "undefined") {
//     showLoading();
//   }
// }

// function afterRequest(): RespondedHandlerRecord<AlovaGenerics> {
//   return {
//     // 请求成功的拦截器
//     // 当使用 `alova/fetch` 请求适配器时，第一个参数接收Response对象
//     // 第二个参数为当前请求的method实例，你可以用它同步请求前后的配置信息
//     onSuccess: async (response, method) => {
//       // console.log("onSuccess baseURL :", method.baseURL);
//       // console.log("onSuccess response ", JSON.stringify(response.data));
//       if (response.data._errno) {
//         if (response.data._errno == 401) {
//           clearLocalStorage();
//           const { push } = useRouter();
//           // 跳转登录
//           push("login");
//         }
//         if (method.config.shwoloading && typeof window !== "undefined") {
//           hideLoading();
//         }
//         toast.error(getErrorMessage(response.data._errno));
//         throw new Error(getErrorMessage(response.data._errno));
//       }
//       if (method.config.shwoloading && typeof window !== "undefined") {
//         hideLoading();
//       }
//       return response.data._param;
//     },

//     // 请求失败的拦截器
//     // 请求错误时将会进入该拦截器。
//     // 第二个参数为当前请求的method实例，你可以用它同步请求前后的配置信息
//     onError: (err, method) => {
//       if (method.config.shwoloading && typeof window !== "undefined") {
//         hideLoading();
//       }
//       console.log("onError:", err, method.baseURL);
//     },

//     // 请求完成的拦截器
//     // 当你需要在请求不论是成功、失败、还是命中缓存都需要执行的逻辑时，可以在创建alova实例时指定全局的`onComplete`拦截器，例如关闭请求 loading 状态。
//     // 接收当前请求的method实例
//     onComplete: async () => {
//       // 处理请求完成逻辑
//       // console.log("onComplete:", method.baseURL);
//     },
//   };
// }

function hallUrlSuffix(
  servername: string,
  func: string,
  params: Record<string, string | number>,
  bToken: boolean = true,
) {
  const UrlCommonParam = getUrlCommonParam(func, bToken);
  if (params) {
    // 设置url参数，处理lang, token重复情况
    Object.entries(params).forEach(([key, value]) => {
      UrlCommonParam.set(key, value.toString());
    });
  }
  return `/mini/_${appID}/${servername}?_func=${func}&${UrlCommonParam.toString()}`;
}

export function getToken() {
  // 1. 优先从 userStore 获取（内存中的最新状态）
  if (userStore.token) {
    return userStore.token;
  }

  // 2. 从多重存储策略获取（已增强的 getLocalStorageItem）
  const localToken = getLocalStorageItem(LocalStorageKey.TOKEN, "");

  if (localToken) {
    return localToken;
  }

  return "";
}

export function clearToken() {
  userStore.token = "";
  removeLocalStorageItem(LocalStorageKey.TOKEN);
  useGlobalCacheCookie().remove(LocalStorageKey.IS_LOGIN);
}

export function clearLocalStorage() {
  removeLocalStorageItem(LocalStorageKey.TOKEN);
  removeLocalStorageItem(LocalStorageKey.USER_STORE);
  removeLocalStorageItem(LocalStorageKey.USER_ID);
}

//获取url 参数
function getUrlCommonParam(func: string, bToken: boolean = true) {
  // TODO 从cookie获取
  let token = isServer ? null : getToken();
  token = token ? token : "pass"; //有token 就使用token，没有token 就使用pass

  const blackList = [
    "NA.GetPage",
    "NA.GetFrontCfgV2",
    "NA.GetActivityConfig",
    "NA.GetComponentList",
    "NA.GetSysConfig",
    "NA.GetFrontConfig",
    "NA.GetComponent",
    "NA.GetPageComponents",
  ];
  if (blackList.includes(func)) {
    token = "pass"; // 白名单不使用token 防止token过期首页黑屏
  }

  // 在服务端使用请求特定的语言获取函数，在客户端使用原有的函数
  let lang = isServer ? getCurrentRequestLangKey() : Number(getLangKey());
  lang ??= 12;
  // TODO apptype完善
  const appType = deviceInfo.app_type;
  // 系统类型 0-未知 1-PC 2-android 3-ios
  const apiv = import.meta.env.VITE_API_VERSION ?? 1;
  const proj = "s";
  let param = `lang=${lang}&token=${token}&os=${deviceInfo.os_type}&at=${appType}&m=${Date.now()}&apiv=${apiv}&proj=${proj}`;
  const rcsParam = (!isServer && getRcsParam()) || "";
  if (rcsParam) {
    param += rcsParam;
  }
  return new URLSearchParams(param);
}

const getNeedAddRcsParam = () => {
  const rcsEntryTime = getLocalStorageItem(LocalStorageKey.RCSEntryTime, 0);
  const lastEntryDate = new Date(rcsEntryTime);
  const now = new Date();
  if (rcsEntryTime == 0) {
    return false;
  }
  if (now.getDate() !== lastEntryDate.getDate()) {
    setLocalStorageItem(LocalStorageKey.RCSEntryTime, 0);
    return false;
  }
  return true;
};

export function getRcsParam() {
  const { getStoredParams } = useUrlStorage();
  let rcsParam = "";
  const addParams = ["adtp", "adid", "adex"];

  if (getNeedAddRcsParam()) {
    addParams.forEach((param) => {
      if (getStoredParams()?.params?.[param]) {
        rcsParam += `&${param}=${getStoredParams()?.params?.[param]}`;
      }
    });
  }
  return rcsParam;
}

/**
 * 将应用ID编码为自定义字符串
 *
 * @param appId - 应用ID（数字）
 * @returns 编码后的字符串
 */

export function encodeAppid(appId: number) {
  const DECIMAL_DICT = "fgHijUvWXAbcdEyzKLMnOpqRst";
  const BASE = 26;
  const result: string[] = [];
  let num = appId;

  do {
    const remainder = num % BASE;
    result.unshift(DECIMAL_DICT[remainder]);
    num = Math.floor(num / BASE);
  } while (num !== 0);

  return result.join("");
}

export function getWSSUrl() {
  const lang = isServer ? getCurrentRequestLangKey() : Number(getLangKey());
  let token = getToken();
  token = token ? token : "pass";
  const wsOrigin =
    import.meta.env.MODE === "development"
      ? import.meta.env.VITE_WSS_SERVER_URL
      : window.location.host;
  const protocol = "wss://";
  return `${protocol}${wsOrigin}/dict/_${appID}/?_func=NA.Event&os=${deviceInfo.os_type}&at=${deviceInfo.app_type}&lang=${lang}&token=${token}`;
}
function shwoloading() {
  throw new Error("Function not implemented.");
}
export function getHallHttpUrl(mod: string, func: string, bToken = true) {
  const baseUrl = getBaseHttpUrl();
  const httpUrlSuffix = hallUrlSuffix(mod, func, {}, bToken);
  // 如果是内网IP场景（baseUrl为空）
  if (baseUrl === "" && !isServer) {
    // httpUrlSuffix 已经包含前导斜杠
    return `${window.location.origin}${httpUrlSuffix}`;
  }
  return `${baseUrl}${httpUrlSuffix}`;
}

export function getPostMsgParam(
  mod: string,
  func: string,
  param: unknown,
  useMode = false,
) {
  const msg: Record<string, unknown> = {};
  if (useMode) {
    msg._mod = mod;
    msg._func = func;
  }
  msg._param = param ? param : {};
  checkHelper.updateChecker();
  msg._check = checkHelper.getNormalChecker();

  return msg;
}

export function getGetMsgParam<
  T extends Record<string, string | number | boolean>,
>(params: T) {
  const keys = Object.keys(params);
  const queryParams = new URLSearchParams();
  if (keys.length > 0) {
    keys.forEach((key) => {
      queryParams.append(key, String(params[key]));
    });
  }
  return `&${queryParams.toString()}`;
}
